import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { usePromiseTracker } from "react-promise-tracker";
import PrivateRoute from "./PrivateRoute";
import { Nav, LoadingOverlay, Notification, Page } from "_components";
import {
  LoginLayout,
  ForgotPasswordLayout,
  RegisterMainLayout,
} from "container/layout";
import { Home } from "container/dashboard";
import Container from "@material-ui/core/Container";
import RegistrationLayout from "container/layout/RegistrationLayout";
import { DashboardLayout } from "container/layout";
import { Configuration } from "container/configurations";
import { EnergyAssistance } from "container/energyAssistance";
import { AccountInquiry } from "container/accountInquiry";
import { getAppMenus } from "_utils";
import { AnnouncementCenter, FAQView } from "container/headers";
import { MapCenter } from "container/mapCenter";
import { Customer } from "container/admin";
import Firm from "container/marketer/summaryAdjustment/byFirm/Firm";
import { ActivityList } from "container/user";
import { SupplierDiversity } from "container/suplierDiversity";
import Service from "container/marketer/services/service";
import { FAQViews } from "../container/loginPage";
import BackButtonHandler from "./BackButtonHandler";
import Box from "@mui/material/Box";
import Footer from "../container/dashboard/Footer";
import { useCheckAdminRole, useCheckRole } from "_utils/role-access";
import { Register } from "container/registration";
import ManageProfileMC from "container/mapCenter/ManageProfileMC";
import Organizations from "container/admin/manageAgency";
import PublicRoute from "./PublicRoute";
import ForecastDrv from "container/marketer/Nominations/forecast-drv/ForecastDrv";
import StorageTracking from "container/marketer/Nominations/storage-tracking/StorageTracking";
import MatrixCompliance from "container/marketer/Nominations/matrix-compliance/MatrixCompliance";
import ReactGA from "react-ga4";
import BaseAndHeatFactorsReport from "container/marketer/Reports/baseAndHeatFactors/BaseAndHeatFactorsReport";
const Users = lazy(() =>
  import("container/admin").then((m) => ({ default: m.Users })),
);
const Announcement = lazy(() =>
  import("container/admin").then((m) => ({ default: m.Announcement })),
);
const Support = lazy(() =>
  import("container/admin").then((m) => ({ default: m.Support })),
);
const FAQ = lazy(() =>
  import("container/admin").then((m) => ({ default: m.FAQ })),
);
const Marketers = lazy(() =>
  import("container/admin").then((m) => ({ default: m.Marketers })),
);
const MarketersGroup = lazy(() =>
  import("container/admin").then((m) => ({ default: m.MarketersGroup })),
);
const PipelineDelivery = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.PipelineDelivery })),
);
const Interruptible = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.Interruptible })),
);
const FileHub = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.FileHub })),
);
const DCReports = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.DCReports })),
);
const Customers = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.Customers })),
);
const CustomerUsage = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.CustomerUsage })),
);
const ForeCastReports = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.ForeCastReports })),
);
const DoNotTrack = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.DoNotTrack })),
);
const SummaryStorageMarketerReport = lazy(() =>
  import("container/marketer").then((m) => ({
    default: m.SummaryStorageMarketerReport,
  })),
);
const MonthlyStorageReports = lazy(() =>
  import("container/marketer").then((m) => ({
    default: m.MonthlyStorageReports,
  })),
);
const CityGateReports = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.CityGateReports })),
);
const NominationCompliance = lazy(() =>
  import("container/marketer").then((m) => ({
    default: m.NominationCompliance,
  })),
);
const PipelineConfirmation = lazy(() =>
  import("container/marketer").then((m) => ({
    default: m.PipelineConfirmation,
  })),
);
const ActivityInterruptible = lazy(() =>
  import("container/marketer").then((m) => ({
    default: m.ActivityInterruptible,
  })),
);
const SNComplianceReport = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.SNComplianceReport })),
);
const SupplierPendingEnrollmentOrDropReports = lazy(() =>
  import("container/marketer").then((m) => ({
    default: m.SupplierPendingEnrollmentOrDropReports,
  })),
);
const SupplierActiveCustomerReport = lazy(() =>
  import("container/marketer").then((m) => ({
    default: m.SupplierActiveCustomerReport,
  })),
);
const Nominations = lazy(() =>
  import("container/marketer").then((m) => ({ default: m.Nominations })),
);
const ManageSeasons = lazy(() =>
  import("container/energyAssistance/pages/ManageSeasons").then((m) => ({
    default: m.ManageSeasons,
  })),
);
const AccountBalanceReport = lazy(() =>
  import("container/energyAssistance/pages/AccountBalanceReport").then((m) => ({
    default: m.AccountBalanceReport,
  })),
);
const EnergyUsageReport = lazy(() =>
  import("container/energyAssistance/pages/EnergyUsageReport").then((m) => ({
    default: m.EnergyUsageReport,
  })),
);
const OutstandingGrantReport = lazy(() =>
  import("container/energyAssistance/pages/OutstandingGrantReport").then(
    (m) => ({ default: m.OutstandingGrantReport }),
  ),
);
const ReconnectionRequestReport = lazy(() =>
  import("container/energyAssistance/pages/ReconnectionRequestReport").then(
    (m) => ({ default: m.ReconnectionRequestReport }),
  ),
);
const PledgeAmountReport = lazy(() =>
  import("container/energyAssistance/pages/PledgeAmountReport").then(
    (m) => ({ default: m.PledgeAmountReport }),
  ),
);
const EmergencyHoldRequestReport = lazy(() =>
  import("container/energyAssistance/pages/EmergencyHoldRequestReport").then(
    (m) => ({ default: m.EmergencyHoldRequestReport }),
  ),
);
const CreateMapRequest = lazy(() =>
  import("container/mapCenter/pages/CreateMapRequest").then((m) => ({
    default: m.CreateMapRequest,
  })),
);
const ProcessMapRequest = lazy(() =>
  import("container/mapCenter/pages/ProcessMapRequest").then((m) => ({
    default: m.ProcessMapRequest,
  })),
);
const ViewMapRequest = lazy(() =>
  import("container/mapCenter/pages/ViewMapRequest").then((m) => ({
    default: m.ViewMapRequest,
  })),
);
const MapRequestReport = lazy(() =>
  import("container/mapCenter/pages/MapRequestReport").then((m) => ({
    default: m.MapRequestReport,
  })),
);
const SecurityReviewReport = lazy(() =>
  import("container/mapCenter/pages/SecurityReviewReport").then((m) => ({
    default: m.SecurityReviewReport,
  })),
);
const SupplierCompanyInfo = lazy(() =>
  import("container/suplierDiversity/pages/SupplierCompanyInfo").then((m) => ({
    default: m.SupplierCompanyInfo,
  })),
);
const RouteList = () => {
  const { isAuthenticated, isInitialized } = useSelector((x) => x.auth);
  const promiseTracker = usePromiseTracker();
  const navigate = useNavigate();
  const [portalID, setPortalID] = useState(null);
  const authUser = useSelector((x) => x.auth?.value);
  const user = authUser?.Data;
  const isMCUserExpired = user?.UserDetails?.Status === "Expired";
  const isMCUserPartiallyApproved =
    user?.UserDetails?.Status === "Partially Approved";
  const isMCUserPartiallyVerified =
    user?.UserDetails?.Status === "Partially Verified";
  const adminList = user?.UserAccess?.filter((access) =>
    ["admin", "reviewer", "owner", "digital-admin"].includes(
      access.Role.toLowerCase(),
    ),
  );
  const isAdmin = user?.UserAccess?.some((access) =>
    access.Role.toLowerCase().includes("admin"),
  );
  const isDigitalAdmin = user?.UserAccess?.some((access) =>
    access.Role.toLowerCase().includes("digital-admin"),
  );

  const location = useLocation();
  useEffect(() => {
    ReactGA.send({
    hitType: "pageview",
    page: location.pathname + location.search,
  });
  }, [location]);
  
  const hasViewAIAgencyAccess = useCheckRole(1, "ViewAIAgency");
  const hasAddAIAgencyAccess = useCheckRole(1, "AddAIAgency");
  const hasUpdateAIAgencyAccess = useCheckRole(1, "UpdateAIAgency");
  const hasDeleteAIAgencyAccess = useCheckRole(1, "DeleteAIAgency");
  
  const hasViewEAAgencyAccess = useCheckRole(2, "ViewEAAgency");
  const hasAddEAAgencyAccess = useCheckRole(2, "AddEAAgency");
  const hasUpdateEAAgencyAccess = useCheckRole(2, "UpdateEAAgency");
  const hasDeleteEAAgencyAccess = useCheckRole(2, "DeleteEAAgency");

  const hasViewMCAgencyAccess = useCheckRole(3, "ViewMapRequestor");
  const hasAddMCAgencyAccess = useCheckRole(3, "AddMapRequestor");
  const hasUpdateMCAgencyAccess = useCheckRole(3, "UpdateMapRequestor");
  const hasDeleteMCAgencyAccess = useCheckRole(3, "DeleteMapRequestor");
  const hasGetDRVForecastAccess = useCheckRole(4, "GetDRVForecast");
  
  const hasSupplierPendingEnrollmentOrDropReportAccess = useCheckRole(
    4,
    "SupplierPendingEnrollmentOrDropReport",
  );
  const hasSupplierActiveCustomersReportAccess = useCheckRole(
    4,
    "SupplierActiveCustomersReport",
  );
  const hasSummarySupplyByCityGateReportAccess = useCheckRole(
    4,
    "SummarySupplyByCityGateReport",
  );
  const hasSummaryStorageByMarketerReportAccess = useCheckRole(
    4,
    "SummaryStorageByMarketerReport",
  );
  const hasSummaryNominationComplianceReportAccess = useCheckRole(
    4,
    "SummaryNominationComplianceReport",
  );
  const hasPipelineConfirmationReportAccess = useCheckRole(
    4,
    "PipelineConfirmationReport",
  );
  const hasNominationComplianceReportAccess = useCheckRole(
    4,
    "NominationComplianceReport",
  );
  const hasMonthlyStorageReportAccess = useCheckRole(4, "MonthlyStorageReport");
  const hasDC_NominationReallocationReportAccess = useCheckRole(
    4,
    "DC_NominationReallocationReport",
  );
  const hasCustomerUsageReportAccess = useCheckRole(4, "CustomerUsageReport");
  const hasAdjustmentActivityInterruptibleReportAccess = useCheckRole(
    4,
    "AdjustmentActivityInterruptibleReport",
  );
  const has5DaysRequirementForecastReportAccess = useCheckRole(
    4,
    "5DaysRequirementForecastReport",
  );
  const hasDoNotTransferReportAccess = useCheckRole(4, "DoNotTransferReport");

  const hasViewCustomerAccountDetailsAccess = useCheckRole(
    4,
    "ViewCustomerAccountDetails",
  );
  const hasViewPipelineDeliveryMatrixAccess = useCheckRole(
    4,
    "ViewPipelineDeliveryMatrix",
  );
  const hasViewPipelineDeliveryGuideAccess = useCheckRole(
    4,
    "ViewPipelineDeliveryGuide",
  );
  const hasViewPipelineNominationAccess = useCheckRole(
    4,
    "ViewPipelineNomination",
  );
  const hasManageStorageTrackingAccess = useCheckRole(4, "ManageStorageTracking");
  const hasViewMatrixComplianceAccess = useCheckRole(4, "ViewMatrixCompliance");
  const hasBaseAndHeatFactorReportAccess = useCheckRole(4, "BaseAndHeatFactorReport");
  const hasViewMarketerAccess = useCheckRole(4, "ViewMarketer");
  const hasViewGroupNominationAccess = useCheckRole(4, "ViewGroupNomination");
  const hasViewMarketerGroupsAccess = useCheckRole(4, "ViewMarketerGroups");
  const hasViewAccountMarketerAssociationAccess = useCheckRole(
    4,
    "ViewAccountMarketerAssociation",
  );
  const hasViewAlphaBetaAndEOMServiceStatusAccess = useCheckRole(
    4,
    "ViewAlphaBetaAndEOMServiceStatus",
  );
  const hasViewFuelChargesAccess = useCheckRole(4, "ViewFuelCharges");
  const hasViewSummaryAdjustmentsByInterruptibleMarketerGroupsAccess =
    useCheckRole(4, "ViewSummaryAdjustmentsByInterruptibleMarketerGroups");
  const haViewSummaryAdjustmentsByFirmMarketerGroupsAccess = useCheckRole(
    4,
    "ViewSummaryAdjustmentsByFirmMarketerGroups",
  );
  const hasActivityLogAccess = useCheckAdminRole("ActivityLog");
  const hasViewUserRolesAccess = useCheckAdminRole("DigitalAdmin");
  const hasViewUserProfilesAccess = useCheckAdminRole("ViewUserProfiles");
  const hasUpdateSupportContactInformationAccess = useCheckAdminRole(
    "UpdateSupportContactInformation",
  );
  const hasViewAnnouncementsAndNotificationsAccess = useCheckAdminRole(
    "ViewAnnouncementsAndNotifications",
  );
  const hasSearchByAccountNumberEnergyAssistanceAccess = useCheckRole(
    2,
    "SearchByAccountNumber",
  );
  const hasViewManageSeasonsAccess = useCheckRole(2, "ViewManageSeasons");
  const hasAccountBalanceReportAccess = useCheckRole(2, "AccountBalanceReport");
  const hasEnergyUsageReportAccess = useCheckRole(2, "EnergyUsageReport");
  const hasOutstandingGrantReportAccess = useCheckRole(
    2,
    "OutstandingGrantsReport",
  );
  const hasReconnectionRequestReportAccess = useCheckRole(
    2,
    "ReconnectionRequestReport",
  );
  const hasCreateMapRequestAccess = useCheckRole(3, "CreateMapRequest");
  const hasProcessMapRequestAccess = useCheckRole(3, "ProcessMapRequest");
  const hasViewMapRequestAccess = useCheckRole(3, "ViewMapRequest");
  const hasMapRequestReportAccess = useCheckRole(3, "MapRequestReport");
  const hasSecurityReviewReportAccess = useCheckRole(3, "SecurityReviewReport");
  const hasViewSupplierSummaryCountAccess = useCheckRole(
    5,
    "SupplierSummaryCount",
  );
  const hasViewSupplierRegisteredAccess = useCheckRole(
    5,
    "SupplierSearchByFilter",
  );
  const hasViewSupplierExpiringCertAccess = useCheckRole(
    5,
    "ExpiringSupplierSearchByFilter",
  );
  const hasViewSupplierCompanyDetailsAccess = useCheckRole(
    5,
    "ViewCompanyDetails",
  );
  const hasAddSupplierCompanyDetailsAccess = useCheckRole(
    5,
    "AddCompanyDetailsSelf",
  );
  const hasUpdateSupplierCompanyDetailsAccess = useCheckRole(
    5,
    "UpdateCompanyDetailsSelf",
  );
  const hasUniversalSearchAccountInquiryAccess = useCheckRole(
    1,
    "UniversalSearch",
  );

  const portalData = adminList
    ? adminList
        ?.map((admin) => ({
          PortalId: admin.PortalId,
          PortalName: admin.PortalName,
          PortalKey: admin.PortalKey,
        }))
        .sort((a, b) => a.PortalName.localeCompare(b.PortalName))
    : [];
  const savedMenuItems = sessionStorage.getItem("appMenuItems");
  const [appMenuItems, setAppMenuItems] = useState([]);

  useEffect(() => {
    const newMenu = savedMenuItems ? JSON.parse(savedMenuItems) : [];
    setAppMenuItems(newMenu);
  }, [savedMenuItems]);

  const handleCardClick = (portalKey, isMBAdmin, path) => {
    const selectedPortal = portalData.find(
      (p) => p.PortalKey.toLowerCase() === portalKey.toLowerCase(),
    );
    const roleAccess = {
      hasSupplierPendingEnrollmentOrDropReportAccess:
        hasSupplierPendingEnrollmentOrDropReportAccess,
      hasSupplierActiveCustomersReportAccess:
        hasSupplierActiveCustomersReportAccess,
      hasSummarySupplyByCityGateReportAccess:
        hasSummarySupplyByCityGateReportAccess,
      hasSummaryStorageByMarketerReportAccess:
        hasSummaryStorageByMarketerReportAccess,
      hasSummaryNominationComplianceReportAccess:
        hasSummaryNominationComplianceReportAccess,
      hasPipelineConfirmationReportAccess: hasPipelineConfirmationReportAccess,
      hasNominationComplianceReportAccess: hasNominationComplianceReportAccess,
      hasMonthlyStorageReportAccess: hasMonthlyStorageReportAccess,
      hasDC_NominationReallocationReportAccess:
        hasDC_NominationReallocationReportAccess,
      hasCustomerUsageReportAccess: hasCustomerUsageReportAccess,
      hasAdjustmentActivityInterruptibleReportAccess:
        hasAdjustmentActivityInterruptibleReportAccess,
      has5DaysRequirementForecastReportAccess:
        has5DaysRequirementForecastReportAccess,
      hasDoNotTransferReportAccess: hasDoNotTransferReportAccess,
      hasViewCustomerAccountDetailsAccess: hasViewCustomerAccountDetailsAccess,
      hasViewPipelineDeliveryMatrixAccess: hasViewPipelineDeliveryMatrixAccess,
      hasViewPipelineDeliveryGuideAccess: hasViewPipelineDeliveryGuideAccess,
      hasViewPipelineNominationAccess: hasViewPipelineNominationAccess,
      hasManageStorageTrackingAccess: hasManageStorageTrackingAccess,
      hasBaseAndHeatFactorReportAccess: hasBaseAndHeatFactorReportAccess,
      hasViewMarketerAccess: hasViewMarketerAccess,
      hasViewGroupNominationAccess: hasViewGroupNominationAccess,
      hasViewMarketerGroupsAccess: hasViewMarketerGroupsAccess,
      hasViewAccountMarketerAssociationAccess:
        hasViewAccountMarketerAssociationAccess,
      hasViewAlphaBetaAndEOMServiceStatusAccess:
        hasViewAlphaBetaAndEOMServiceStatusAccess,
      hasViewFuelChargesAccess: hasViewFuelChargesAccess,
      hasViewSummaryAdjustmentsByInterruptibleMarketerGroupsAccess:
        hasViewSummaryAdjustmentsByInterruptibleMarketerGroupsAccess,
      haViewSummaryAdjustmentsByFirmMarketerGroupsAccess:
        haViewSummaryAdjustmentsByFirmMarketerGroupsAccess,
      hasViewUserProfilesAccess: hasViewUserProfilesAccess,
      hasUpdateSupportContactInformationAccess:
        hasUpdateSupportContactInformationAccess,
      hasCreateMapRequestAccess: hasCreateMapRequestAccess,
      hasProcessMapRequestAccess: hasProcessMapRequestAccess,
      hasViewMapRequestAccess: hasViewMapRequestAccess,
      hasMapRequestReportAccess: hasMapRequestReportAccess,
      hasSecurityReviewReportAccess: hasSecurityReviewReportAccess,
      hasViewManageSeasonsAccess: hasViewManageSeasonsAccess,
      hasAccountBalanceReportAccess: hasAccountBalanceReportAccess,
      hasEnergyUsageReportAccess: hasEnergyUsageReportAccess,
      hasOutstandingGrantReportAccess: hasOutstandingGrantReportAccess,
      hasReconnectionRequestReportAccess: hasReconnectionRequestReportAccess,
      hasViewSupplierSummaryCountAccess: hasViewSupplierSummaryCountAccess,
      hasViewSupplierRegisteredAccess: hasViewSupplierRegisteredAccess,
      hasViewSupplierExpiringCertAccess: hasViewSupplierExpiringCertAccess,
      hasViewSupplierCompanyDetailsAccess: hasViewSupplierCompanyDetailsAccess,
      hasAddSupplierCompanyDetailsAccess: hasAddSupplierCompanyDetailsAccess,
      hasUpdateSupplierCompanyDetailsAccess:
        hasUpdateSupplierCompanyDetailsAccess,
      hasUniversalSearchAccountInquiryAccess,
      hasSearchByAccountNumberEnergyAssistanceAccess,
      hasViewAnnouncementsAndNotificationsAccess,
      hasViewEAAgencyAccess,
      hasAddEAAgencyAccess,
      hasUpdateEAAgencyAccess,
      hasDeleteEAAgencyAccess,
      hasViewAIAgencyAccess,
      hasAddAIAgencyAccess,
      hasUpdateAIAgencyAccess,
      hasDeleteAIAgencyAccess,
      hasViewMCAgencyAccess,
      hasAddMCAgencyAccess,
      hasUpdateMCAgencyAccess,
      hasDeleteMCAgencyAccess,
      hasGetDRVForecastAccess
    };
    const menus =
      getAppMenus(
        portalKey,
        isMBAdmin,
        roleAccess,
        isAdmin,
        isMCUserExpired,
        isMCUserPartiallyApproved,
        isMCUserPartiallyVerified,
      ) || [];
    if (hasViewUserProfilesAccess) {
      const userMenuIndex = menus.findIndex((menu) => menu.name === "Users");
      if (userMenuIndex !== -1) {
        menus[userMenuIndex] = getUserMenus(menus[userMenuIndex]);
      }
    }

    const newItem = {
      name: "Role Management",
      link: "configuration",
      order: 10,
    };
    if (hasViewUserRolesAccess && isDigitalAdmin)
      menus.map((menu) => {
        if (menu.name === "Users") {
          menu.items.push(newItem);
        }
        return menu;
      });
    setPortalID(selectedPortal?.PortalId);
    setAppMenuItems(menus);
    sessionStorage.setItem("appMenuItems", JSON.stringify(menus));
    navigate(`/${path}`);
  };

  const getUserMenus = (userMenu) => {
    const menuItems = portalData.map((p, index) => ({
      name: p.PortalName,
      link: `userprofile/${p.PortalId}`,
      orderID: index,
    }));
    userMenu = { ...userMenu, items: menuItems };
    return userMenu;
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <BackButtonHandler />
      {isInitialized && (
        <Nav
          appMenuItems={appMenuItems}
          isAuthenticated={isAuthenticated}
          portalID={portalID}
        />
      )}
      <Notification />
      <LoadingOverlay
        loading={promiseTracker.promiseInProgress}
      ></LoadingOverlay>
      <div style={{ flex: 1 }} className="container inputtags admincontent">
        <Container maxWidth="lg" className="admincontentContainer">
          <Box
            sx={{
              display: {
                xs: "none", // hidden on extra-small
                md: "block", // visible from medium and up (md, lg, xl)
              },
            }}
          >
            <DashboardLayout appMenuItems={appMenuItems} />
          </Box>
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              {/* Public routes */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate to="/home" replace />
                  ) : (
                    <Page defaultTitle="LOGIN ">
                      <LoginLayout />
                    </Page>
                  )
                }
              />
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/home" replace />
                  ) : (
                    <Page defaultTitle="LOGIN ">
                      <LoginLayout />
                    </Page>
                  )
                }
              />

              <Route
                element={
                  <PublicRoute
                    isAuthenticated={isAuthenticated}
                    isInitialized={isInitialized}
                  />
                }
              >
                <Route
                  path="/registration/*"
                  element={
                    <Page defaultTitle="Registration">
                      <RegistrationLayout />
                    </Page>
                  }
                />
                <Route
                  path="/forgotpassword/*"
                  element={
                    <Page defaultTitle="Forgot Password">
                      <ForgotPasswordLayout />
                    </Page>
                  }
                />
                <Route element={<RegisterMainLayout />}>
                  <Route
                    path="/register"
                    element={
                      <Page defaultTitle="Registration">
                        <Register />
                      </Page>
                    }
                  />
                </Route>
                <Route
                  path="/faqViews"
                  element={
                    <Page defaultTitle="FAQ Views">
                      <FAQViews />
                    </Page>
                  }
                />
              </Route>

              {/* Private routes */}
              <Route
                element={<PrivateRoute isAuthenticated={isAuthenticated} />}
              >
                <Route
                  path="/faqView"
                  element={
                    <Page defaultTitle="FAQ View">
                      <FAQView />
                    </Page>
                  }
                />
                <Route
                  path="/home"
                  element={
                    <Page defaultTitle="HOME ">
                      <Home handleCardClick={handleCardClick} />
                    </Page>
                  }
                />
                {hasViewUserProfilesAccess && (
                  <Route
                    path="/userprofile/:portal"
                    element={
                      <Page defaultTitle="USERPROFILE ">
                        <Users />
                      </Page>
                    }
                  />
                )}
                {hasViewUserRolesAccess && (
                  <Route
                    path="/configuration"
                    element={
                      <Page defaultTitle="CONFIGURATION ">
                        <Configuration />
                      </Page>
                    }
                  />
                )}
                {isAdmin &&
                  hasViewEAAgencyAccess &&
                  hasAddEAAgencyAccess &&
                  hasUpdateEAAgencyAccess &&
                  hasDeleteEAAgencyAccess && (
                    <Route
                      path="/manage-ea-agency"
                      element={
                        <Page defaultTitle="Manage Energy Assistance Agency ">
                          <Organizations />
                        </Page>
                      }
                    />
                  )}
                {isAdmin &&
                  hasViewAIAgencyAccess &&
                  hasAddAIAgencyAccess &&
                  hasUpdateAIAgencyAccess &&
                  hasDeleteAIAgencyAccess && (
                    <Route
                      path="/manage-ai-agency"
                      element={
                        <Page defaultTitle="Manage Account Inquiry Agency ">
                          <Organizations />
                        </Page>
                      }
                    />
                  )}
                {isAdmin &&
                  hasViewMCAgencyAccess &&
                  hasAddMCAgencyAccess &&
                  hasUpdateMCAgencyAccess &&
                  hasDeleteMCAgencyAccess && (
                  <Route
                    path="/manage-map-requestor"
                    element={
                      <Page defaultTitle="Manage Map Requestor ">
                        <Organizations />
                      </Page>
                    }
                  />
                )}
                {isAdmin && (
                  <Route
                    path="/manage-marketer-pipeline"
                    element={
                      <Page defaultTitle="Manage Marketer Pipelines ">
                        <Organizations />
                      </Page>
                    }
                  />
                )}
                {hasViewAnnouncementsAndNotificationsAccess && (
                  <Route
                    path="/announcement"
                    element={
                      <Page defaultTitle="ANNOUNCEMENT ">
                        <Announcement />
                      </Page>
                    }
                  />
                )}
                {hasViewMarketerAccess && (
                  <Route
                    path="/marketer"
                    element={
                      <Page defaultTitle="MARKETER ">
                        <Marketers />
                      </Page>
                    }
                  />
                )}
                {hasViewMarketerGroupsAccess && (
                  <Route
                    path="/marketerGroup"
                    element={
                      <Page defaultTitle="MARKETERGROUP ">
                        <MarketersGroup />
                      </Page>
                    }
                  />
                )}
                {hasUpdateSupportContactInformationAccess && (
                  <Route
                    path="/faqCreate"
                    element={
                      <Page defaultTitle="FAQCREATE ">
                        <FAQ />
                      </Page>
                    }
                  />
                )}
                {hasUpdateSupportContactInformationAccess && (
                  <Route
                    path="/support"
                    element={
                      <Page defaultTitle="SUPPORT ">
                        <Support />
                      </Page>
                    }
                  />
                )}
                {hasSearchByAccountNumberEnergyAssistanceAccess && (
                  <Route
                    path="/dashboardEA"
                    element={
                      <Page defaultTitle="ENERGY ASSISTANCE - Dashboard">
                        <EnergyAssistance />
                      </Page>
                    }
                  />
                )}
                {hasViewManageSeasonsAccess && (
                  <Route
                    path="/ea-manage-seasons"
                    element={
                      <Page defaultTitle="ENERGY ASSISTANCE - Manage Seasons">
                        <ManageSeasons />
                      </Page>
                    }
                  />
                )}
                {hasAccountBalanceReportAccess && (
                  <Route
                    path="/ea-reports-account-balance"
                    element={
                      <Page defaultTitle="ENERGYASSISTANCE - Account Balance Report">
                        <AccountBalanceReport />
                      </Page>
                    }
                  />
                )}
                {hasEnergyUsageReportAccess && (
                  <Route
                    path="/ea-reports-energy-usage"
                    element={
                      <Page defaultTitle="ENERGYASSISTANCE - Energy Usage Report">
                        <EnergyUsageReport />
                      </Page>
                    }
                  />
                )}
                {hasOutstandingGrantReportAccess && (
                  <Route
                    path="ea-reports-outstanding-grant"
                    element={
                      <Page defaultTitle="ENERGYASSISTANCE - Outstanding Grants Report">
                        <OutstandingGrantReport />
                      </Page>
                    }
                  />
                )}
                {hasReconnectionRequestReportAccess && (
                  <Route
                    path="/ea-reports-reconnection-request"
                    element={
                      <Page defaultTitle="ENERGYASSISTANCE - Reconnection Request Report">
                        <ReconnectionRequestReport />
                      </Page>
                    }
                  />
                )}
                {hasReconnectionRequestReportAccess && (
                  <Route
                    path="/ea-reports-pledge-amount"
                    element={
                      <Page defaultTitle="ENERGYASSISTANCE - Pledge Amount Request">
                        <PledgeAmountReport />
                      </Page>
                    }
                  />
                )}
                {hasReconnectionRequestReportAccess && (
                  <Route
                    path="/ea-reports-emergency-hold-request"
                    element={
                      <Page defaultTitle="ENERGYASSISTANCE - Emergency Hold Request Report">
                        <EmergencyHoldRequestReport />
                      </Page>
                    }
                  />
                )}
                {hasUniversalSearchAccountInquiryAccess && (
                  <Route
                    path="/dashboardAI"
                    element={
                      <Page defaultTitle="ACCOUNTINQUIRY ">
                        <AccountInquiry />
                      </Page>
                    }
                  />
                )}
                {(hasViewMapRequestAccess ||
                  isMCUserPartiallyVerified ||
                  isMCUserExpired ||
                  isMCUserPartiallyApproved) && (
                  <Route
                    path="/dashboardMC"
                    element={
                      <Page defaultTitle="MAPCENTER ">
                        <MapCenter />
                      </Page>
                    }
                  />
                )}
                {hasCreateMapRequestAccess &&
                  !isMCUserExpired &&
                  !isMCUserPartiallyApproved &&
                  !isMCUserPartiallyVerified && (
                    <Route
                      path="/map-request-create"
                      element={
                        <Page defaultTitle="MAPCENTER - Map Request">
                          <CreateMapRequest />
                        </Page>
                      }
                    />
                  )}
                {hasProcessMapRequestAccess &&
                  !isMCUserExpired &&
                  !isMCUserPartiallyApproved &&
                  !isMCUserPartiallyVerified && (
                    <Route
                      path="/map-request-process/:id"
                      element={
                        <Page defaultTitle="MAPCENTER - Map Request">
                          <ProcessMapRequest />
                        </Page>
                      }
                    />
                  )}
                {hasViewMapRequestAccess &&
                  !isMCUserExpired &&
                  !isMCUserPartiallyApproved &&
                  !isMCUserPartiallyVerified && (
                    <Route
                      path="/map-request-view/:id"
                      element={
                        <Page defaultTitle="MAPCENTER - Map Request">
                          <ViewMapRequest />
                        </Page>
                      }
                    />
                  )}
                {hasMapRequestReportAccess &&
                  !isMCUserExpired &&
                  !isMCUserPartiallyApproved &&
                  !isMCUserPartiallyVerified && (
                    <Route
                      path="/map-request-report"
                      element={
                        <Page defaultTitle="MAPCENTER - Map Request Report">
                          <MapRequestReport />
                        </Page>
                      }
                    />
                  )}
                {(isMCUserExpired ||
                  isMCUserPartiallyApproved ||
                  isMCUserPartiallyVerified) && (
                  <Route
                    path="/:portalkey/complete-registration"
                    element={
                      <Page defaultTitle="MAPCENTER - Map Request Report">
                        <ManageProfileMC />
                      </Page>
                    }
                  />
                )}
                {hasSecurityReviewReportAccess &&
                  !isMCUserExpired &&
                  !isMCUserPartiallyApproved &&
                  !isMCUserPartiallyVerified && (
                    <Route
                      path="/security-review-report"
                      element={
                        <Page defaultTitle="MAPCENTER - Security Review Report">
                          <SecurityReviewReport />
                        </Page>
                      }
                    />
                  )}
                {hasViewSupplierSummaryCountAccess &&
                  hasViewSupplierRegisteredAccess &&
                  hasViewSupplierExpiringCertAccess && (
                    <Route
                      path="/dashboardSD"
                      element={
                        <Page defaultTitle="SUPPLIERDIVERSITY ">
                          <SupplierDiversity />
                        </Page>
                      }
                    />
                  )}
                {hasViewSupplierCompanyDetailsAccess && (
                  <Route
                    path="/sd-view-supplier-info"
                    element={
                      <Page defaultTitle="SUPPLIERDIVERSITY ">
                        <SupplierCompanyInfo isAdmin={isAdmin} />
                      </Page>
                    }
                  />
                )}
                {hasAddSupplierCompanyDetailsAccess &&
                  hasUpdateSupplierCompanyDetailsAccess && (
                    <Route
                      path="/sd-manage-supplier-info"
                      element={
                        <Page defaultTitle="SUPPLIERDIVERSITY ">
                          <SupplierCompanyInfo />
                        </Page>
                      }
                    />
                  )}
                {hasViewAnnouncementsAndNotificationsAccess && (
                  <Route
                    path="/notification"
                    element={
                      <Page defaultTitle="NOTIFICATION ">
                        <AnnouncementCenter />
                      </Page>
                    }
                  />
                )}
                {hasViewPipelineDeliveryMatrixAccess && (
                  <Route
                    path="/pipelinedelivery"
                    element={
                      <Page defaultTitle="PIPELINEREDELIVERY ">
                        <PipelineDelivery />
                      </Page>
                    }
                  />
                )}
                {hasViewAccountMarketerAssociationAccess && (
                  <Route
                    path="/customer"
                    element={
                      <Page defaultTitle="CUSTOMER ">
                        <Customer />
                      </Page>
                    }
                  />
                )}
                {haViewSummaryAdjustmentsByFirmMarketerGroupsAccess && (
                  <Route
                    path="/byfirm"
                    element={
                      <Page defaultTitle="BYFIRM ">
                        <Firm />
                      </Page>
                    }
                  />
                )}
                {hasViewSummaryAdjustmentsByInterruptibleMarketerGroupsAccess && (
                  <Route
                    path="/byInterruptible"
                    element={
                      <Page defaultTitle="BYINTERRUPTIBLE ">
                        <Interruptible />
                      </Page>
                    }
                  />
                )}
                <Route
                  path="/FileHub"
                  element={
                    <Page defaultTitle="FILEHUB ">
                      <FileHub />
                    </Page>
                  }
                />
                {hasDC_NominationReallocationReportAccess && (
                  <Route
                    path="/dcreports"
                    element={
                      <Page defaultTitle="DCNOMINATIONREPORTS ">
                        <DCReports />
                      </Page>
                    }
                  />
                )}
                {hasCustomerUsageReportAccess && (
                  <Route
                    path="/csreports"
                    element={
                      <Page defaultTitle="CUSTOMERUSAGEREPORTS ">
                        <CustomerUsage />
                      </Page>
                    }
                  />
                )}
                {has5DaysRequirementForecastReportAccess && (
                  <Route
                    path="/ForeCastReports"
                    element={
                      <Page defaultTitle="FORECASTREPORTS ">
                        <ForeCastReports />
                      </Page>
                    }
                  />
                )}
                {hasSummaryNominationComplianceReportAccess && (
                  <Route
                    path="/SummaryNominationComplianceReport"
                    element={
                      <Page defaultTitle="SUMMARYNOMINATIONCOMPLIANCEREPORT ">
                        <SNComplianceReport />
                      </Page>
                    }
                  />
                )}
                {hasSupplierActiveCustomersReportAccess && (
                  <Route
                    path="/SupplierActiveCustomer"
                    element={
                      <Page defaultTitle="SUPPLIERACTIVECUSTOMER ">
                        <SupplierActiveCustomerReport />
                      </Page>
                    }
                  />
                )}
                {hasSummaryStorageByMarketerReportAccess && (
                  <Route
                    path="/summaryStorage"
                    element={
                      <Page defaultTitle="SUMMARYSTORAGE ">
                        <SummaryStorageMarketerReport />
                      </Page>
                    }
                  />
                )}
                {hasViewCustomerAccountDetailsAccess && (
                  <Route
                    path="/customerDetails"
                    element={
                      <Page defaultTitle="CUSTOMERDETAILS ">
                        <Customers />
                      </Page>
                    }
                  />
                )}
                {hasMonthlyStorageReportAccess && (
                  <Route
                    path="/monthlyreports"
                    element={
                      <Page defaultTitle="MONTHLYREPORTS ">
                        <MonthlyStorageReports />
                      </Page>
                    }
                  />
                )}
                {hasSummarySupplyByCityGateReportAccess && (
                  <Route
                    path="/citygatereports"
                    element={
                      <Page defaultTitle="CITYGATEREPORTS ">
                        <CityGateReports />
                      </Page>
                    }
                  />
                )}
                {hasNominationComplianceReportAccess && (
                  <Route
                    path="/nominationcompliance"
                    element={
                      <Page defaultTitle="NOMINATIONCOMPLIANCE ">
                        <NominationCompliance />
                      </Page>
                    }
                  />
                )}
                {hasPipelineConfirmationReportAccess && (
                  <Route
                    path="/pipelineconfirmation"
                    element={
                      <Page defaultTitle="PIPELINECONFIRMATION ">
                        <PipelineConfirmation />
                      </Page>
                    }
                  />
                )}
                {hasAdjustmentActivityInterruptibleReportAccess && (
                  <Route
                    path="/activityinterruptible"
                    element={
                      <Page defaultTitle="ACTIVITYINTERRUPTIBLE ">
                        <ActivityInterruptible />
                      </Page>
                    }
                  />
                )}
                {hasBaseAndHeatFactorReportAccess && (
                  <Route
                    path="/base-and-heat-factor-report"
                    element={
                      <Page defaultTitle="BASEANDHEATFACTORREPORT">
                        <BaseAndHeatFactorsReport />
                      </Page>
                    }
                  />
                )}
                {hasSupplierPendingEnrollmentOrDropReportAccess && (
                  <Route
                    path="/supplierPendingEnrollmentOrDrop"
                    element={
                      <Page defaultTitle="SUPPLIERPENDINGENROLLMENTORDROP ">
                        <SupplierPendingEnrollmentOrDropReports />
                      </Page>
                    }
                  />
                )}
                {hasDoNotTransferReportAccess && (
                  <Route
                    path="/dntreports"
                    element={
                      <Page defaultTitle="DONOTTRACKREPORTS ">
                        <DoNotTrack />
                      </Page>
                    }
                  />
                )}
                {hasActivityLogAccess && (
                  <Route
                    path="/activityLog/:portalID"
                    element={<ActivityList />}
                  />
                )}
                {hasViewAlphaBetaAndEOMServiceStatusAccess && (
                  <Route
                    path="/services"
                    element={
                      <Page defaultTitle="SERVICES ">
                        <Service />
                      </Page>
                    }
                  />
                )}
                {hasViewPipelineNominationAccess && (
                  <Route
                    path="/nomination"
                    element={
                      <Page defaultTitle="NOMINATION ">
                        <Nominations />
                      </Page>
                    }
                  />
                )}
                {hasViewPipelineNominationAccess && (
                  <Route
                    path="/forecast-drv"
                    element={
                      <Page defaultTitle="NOMINATION ">
                        <ForecastDrv />
                      </Page>
                    }
                  />
                )}
                {hasManageStorageTrackingAccess && (
                  <Route
                    path="/storage-tracking"
                    element={
                      <Page defaultTitle="NOMINATION ">
                        <StorageTracking />
                      </Page>
                    }
                  />
                )}
                {hasViewMatrixComplianceAccess && (
                  <Route
                    path="/matrix-compliance"
                    element={
                      <Page defaultTitle="NOMINATION ">
                        <MatrixCompliance />
                      </Page>
                    }
                  />
                )}
              </Route>

              {/* Catch-all routes **/}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Box className="clear-both"></Box>
        </Container>
      </div>
      <div className="container inputtags">{isAuthenticated && <Footer />}</div>
    </div>
  );
};

export default RouteList;
